import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer } from "lucide-react";
import { api, errMsg, rp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { VoucherQr } from "@/components/VoucherQr";

export const LAYOUTS = {
  10: { cols: 2, rows: 5, h: "54mm", code: "28pt", brand: "11pt", foot: "10pt", price: "22pt", qr: 78 },
  20: { cols: 4, rows: 5, h: "54mm", code: "22pt", brand: "9pt", foot: "9pt", price: "17pt", qr: 60 },
  40: { cols: 4, rows: 10, h: "27mm", code: "15pt", brand: "7pt", foot: "7pt", price: "12pt", qr: 38 },
  60: { cols: 5, rows: 12, h: "22mm", code: "12pt", brand: "6pt", foot: "6pt", price: "10pt", qr: 30 },
};

/** Harga yang dicetak di kartu selalu Harga User (harga asli untuk pembeli). */
export const cardPrice = (v, settings) => {
  const pkg = (settings.packages || []).find((p) => (p.profile || p.name) === v.profile);
  return Number(pkg?.price_user ?? settings.voucher_price_user ?? v.price ?? 0);
};

/** "1d 12h" / "02:00:00" -> "1 Hari 12 Jam" */
export const fmtDur = (s) => {
  const v = String(s || "").trim();
  if (!v) return "";
  if (/^\d{1,3}:\d{2}(:\d{2})?$/.test(v)) {
    const [h, m] = v.split(":");
    return `${Number(h) ? `${Number(h)} Jam ` : ""}${Number(m) ? `${Number(m)} Menit` : ""}`.trim() || v;
  }
  const map = { w: "Minggu", d: "Hari", h: "Jam", m: "Menit", s: "Detik" };
  const parts = [...v.matchAll(/(\d+)\s*([wdhms])/gi)].map(([, n, u]) => `${n} ${map[u.toLowerCase()]}`);
  return parts.length ? parts.join(" ") : v;
};

export default function PrintVouchers() {
  const [params] = useSearchParams();
  const batch = params.get("batch") || "";
  const only = (params.get("users") || "").split(",").filter(Boolean);
  const [vouchers, setVouchers] = useState([]);
  const [settings, setSettings] = useState({});
  const [profMap, setProfMap] = useState({});
  const [perPage, setPerPage] = useState(40);
  const [thermal, setThermal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [v, s, p] = await Promise.all([
          api.get(`/reports/vouchers?limit=400${batch ? `&batch=${encodeURIComponent(batch)}` : ""}`),
          api.get("/settings"),
          api.get("/hotspot/profiles"),
        ]);
        setVouchers(only.length ? v.data.filter((x) => only.includes(x.username)) : v.data);
        setSettings(s.data);
        setProfMap(Object.fromEntries(p.data.map((x) => [x.name, x.limit_uptime || ""])));
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, [batch]);

  const brand = settings.voucher_design?.brand || settings.wifi_name || "ALPHANET";
  const L = LAYOUTS[perPage];
  const durOf = (v) => fmtDur(v.limit_uptime || profMap[v.profile] || "");
  const pages = [];
  for (let i = 0; i < vouchers.length; i += perPage) pages.push(vouchers.slice(i, i + perPage));

  return (
    <div className="min-h-screen bg-background print:bg-white">
      <div className="mx-auto max-w-[210mm] px-4 py-6 print:hidden">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Button asChild variant="ghost" size="sm" className="rounded-full"><Link to="/hotspot?tab=generate"><ArrowLeft className="mr-1 h-4 w-4" /> Kembali</Link></Button>
            <h1 className="mt-2 font-display text-2xl font-semibold">Cetak Kartu Voucher</h1>
            <p className="text-sm text-muted-foreground" data-testid="print-info">
              {batch ? `Batch ${batch} · ` : only.length ? "Voucher terpilih · " : "Semua voucher · "}{vouchers.length} kartu ·{" "}
              {thermal ? "mode struk thermal 58mm (1 voucher / struk)" : `${pages.length} lembar A4 (${perPage} kartu/lembar)`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!thermal && [10, 20, 40, 60].map((n) => (
              <Button
                key={n} size="sm" variant={perPage === n ? "default" : "outline"} className="rounded-full"
                data-testid={`per-page-${n}`} onClick={() => setPerPage(n)}
              >
                {n}/lembar
              </Button>
            ))}
            <Button
              size="sm" variant={thermal ? "default" : "outline"} className="rounded-full"
              data-testid="thermal-mode" onClick={() => setThermal(!thermal)}
            >
              Thermal 58mm
            </Button>
            <Button onClick={() => window.print()} data-testid="print-now" className="rounded-full bg-gradient-to-r from-sky-400 via-violet-400 to-fuchsia-400 text-white">
              <Printer className="mr-1 h-4 w-4" /> Cetak / Simpan PDF
            </Button>
          </div>
        </div>
      </div>

      {thermal ? (
        <div id="print-area" data-testid="print-thermal" className="mx-auto w-[58mm]">
          {vouchers.map((v) => (
            <div key={v.id || v.username} className="receipt">
              <div className="r-brand">{brand}</div>
              <div className="r-label">KODE VOUCHER</div>
              <div className="r-code">{v.username}</div>
              <div className="r-qr"><VoucherQr v={v} loginUrl={settings.hotspot_login_url || ""} size={120} /></div>
              <div className="r-note">Scan QR untuk login otomatis</div>
              {durOf(v) && <div className="r-row"><span>Durasi</span><span>{durOf(v)}</span></div>}
              <div className="r-price">{rp(cardPrice(v, settings))}</div>
              <div className="r-note">Sambungkan WiFi lalu masukkan kode di halaman login.</div>
              {settings.cs_wa && <div className="r-note">CS/WA: {settings.cs_wa}</div>}
              <div className="r-cut">- - - - - - - - - - - - - - -</div>
            </div>
          ))}
          {!vouchers.length && <p className="p-8 text-center text-sm text-muted-foreground print:hidden">Belum ada voucher untuk dicetak.</p>}
        </div>
      ) : (
        <div id="print-area" data-testid="print-sheet" className="mx-auto max-w-[210mm]">
          {pages.map((page) => (
            <div key={`sheet-${page[0]?.id || page[0]?.username}`} className="sheet border-l border-t border-dashed border-neutral-400" style={{ gridTemplateColumns: `repeat(${L.cols}, minmax(0,1fr))`, display: "grid" }}>
              {page.map((v) => (
                <div key={v.id || v.username} className="cell border-b border-r border-dashed border-neutral-400">
                  <div className="brand">{brand}</div>
                  <div className="mid">
                    <div className="code">{v.username}</div>
                    <div className="qr"><VoucherQr v={v} loginUrl={settings.hotspot_login_url || ""} size={L.qr} /></div>
                  </div>
                  <div className="foot">
                    <span className="dur">{durOf(v)}</span>
                    <span className="price">{rp(cardPrice(v, settings))}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
          {!vouchers.length && <p className="p-8 text-center text-sm text-muted-foreground print:hidden">Belum ada voucher untuk dicetak.</p>}
        </div>
      )}

      <style>{`
        .sheet { width: 200mm; }
        .cell {
          height: ${L.h}; background: #fff; color: #111;
          display: flex; flex-direction: column; align-items: center; justify-content: space-between;
          padding: 2mm 1.5mm;
        }
        .cell .brand { font-size: ${L.brand}; letter-spacing: .12em; text-transform: uppercase; color: #555; font-weight: 600; }
        .cell .mid { display: flex; align-items: center; justify-content: space-between; gap: 1.5mm; width: 100%; overflow: hidden; }
        .cell .qr { background: #fff; line-height: 0; flex: none; }
        .cell .code { font-family: "JetBrains Mono", monospace; font-size: ${L.code}; font-weight: 800; letter-spacing: .06em; line-height: 1; flex: 1 1 auto; min-width: 0; text-align: center; }
        .cell .dur { font-size: ${L.foot}; color: #111; font-weight: 600; }
        .cell .price { font-size: ${L.price}; font-weight: 900; color: #000; line-height: 1; letter-spacing: -.01em; }
        .cell .foot { width: 100%; display: flex; align-items: flex-end; justify-content: space-between; font-size: ${L.foot}; color: #444; }
        .receipt { width: 58mm; background: #fff; color: #000; padding: 3mm 3mm 1mm; text-align: center; }
        .receipt .r-brand { font-size: 11pt; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
        .receipt .r-label { margin-top: 2mm; font-size: 7pt; letter-spacing: .2em; color: #333; }
        .receipt .r-code { font-family: "JetBrains Mono", monospace; font-size: 24pt; font-weight: 800; letter-spacing: .12em; line-height: 1.1; }
        .receipt .r-qr { display: flex; justify-content: center; margin-top: 2mm; line-height: 0; }
        .receipt .r-row { display: flex; justify-content: space-between; font-size: 8pt; margin-top: 1mm; }
        .receipt .r-price { margin-top: 1.5mm; font-size: 20pt; font-weight: 900; letter-spacing: -.01em; }
        .receipt .r-note { margin-top: 1.5mm; font-size: 7pt; color: #222; }
        .receipt .r-cut { margin-top: 2mm; font-size: 7pt; color: #888; }
        @media print {
          @page { size: ${thermal ? "58mm auto" : "A4"}; margin: ${thermal ? "0" : "5mm"}; }
          body { background: #fff; }
          .sheet { page-break-after: always; }
          .receipt { page-break-after: always; }
        }
      `}</style>
    </div>
  );
}
